import localhost from "../../../apis/localhost";
import { isNonAdvancingCursor } from "../../../constants/sharepointAccess";
import {
  selectSharepointAccessResults,
  selectSharepointData,
  selectSharepointSearch,
} from "../selectors";
import {
  resetSharepointSearch,
  setIsSearching,
  setSearchLoadMoreError,
  setSearchPagination,
  setSharepointData,
  setSharepointError,
  setSharepointLoading,
} from "../sharepointSlice";
import { checkSiteAccess } from "./checkSiteAccess";
import { CONNECTIVITY_ERROR, LIMIT, SKIP_REDIRECT } from "./constants";
import { fetchSharepointSites } from "./fetchSharepointSites";
import { handledPolicyError } from "./policyErrors";
import {
  applyPolicyInfo,
  beginRequest,
  isCurrentRequest,
  statusOf,
  withPendingAccess,
} from "./utils";

export const searchSharepoint = () => async (dispatch, getState) => {
  const requestId = beginRequest(dispatch, getState);
  try {
    dispatch(setSharepointLoading(true));
    dispatch(setSharepointError(null));
    dispatch(setSearchLoadMoreError(false));
    const {
      value,
      filters,
      results: searchResults,
    } = selectSharepointSearch(getState());
    const { categories, from, to, author, tags } = filters;

    dispatch(setIsSearching(true));

    const offset = searchResults.pagination.nextStartRow;
    const params = {
      search: value !== "" ? value : undefined,
      itemTypes: categories?.length > 0 ? categories.join(",") : undefined,
      dateFrom: from !== "" ? from.toISOString() : undefined,
      dateTo: to !== "" ? to?.toISOString() : undefined,
      authorName: author !== "" ? author : undefined,
      tags: tags?.length > 0 ? tags.join(",") : undefined,
      limit: LIMIT,
      offset,
    };

    const response = await localhost.get("/sharepoint/search", {
      params,
      ...SKIP_REDIRECT,
    });

    if (!isCurrentRequest(getState, requestId)) {
      return;
    }

    if (response.status !== 200) {
      const error = new Error();
      error.code = response.status;
      throw error;
    }

    const { data = {} } = response || {};
    const {
      results = [],
      pagination = {},
      restricted = false,
      policyRevision = null,
    } = data;
    if (applyPolicyInfo(dispatch, getState, restricted, policyRevision)) {
      dispatch(fetchSharepointSites());
      return;
    }

    const rows = withPendingAccess(
      results,
      restricted,
      selectSharepointAccessResults(getState())
    );
    const isNextPage = searchResults.pagination.hasMore;
    if (isNextPage) {
      const oldData = selectSharepointData(getState()) || [];
      dispatch(setSharepointData([...oldData, ...rows]));
    } else {
      dispatch(setSharepointData(rows));
    }

    const nonAdvancing = isNonAdvancingCursor(pagination, offset);
    const hasMore = Boolean(pagination.hasMore) && !nonAdvancing;
    dispatch(setSearchPagination({ pagination: { ...pagination, hasMore } }));
    if (nonAdvancing) {
      dispatch(setSearchLoadMoreError(true));
    }
    if (restricted) {
      dispatch(checkSiteAccess());
    }
  } catch (error) {
    if (!isCurrentRequest(getState, requestId)) {
      return;
    }
    if (handledPolicyError(dispatch, error)) {
      return;
    }
    const code = statusOf(error);
    if (code >= 500 && code < 600) {
      dispatch(setSharepointError(new Error(CONNECTIVITY_ERROR)));
    } else {
      dispatch(setSharepointError(new Error("Error searching SharePoint")));
    }
    dispatch(setSharepointData([]));
    dispatch(resetSharepointSearch());
  } finally {
    if (isCurrentRequest(getState, requestId)) {
      dispatch(setSharepointLoading(false));
    }
  }
};
